"use client"

import { useId, type ReactNode } from "react"
import {
  Select as UiSelect,
  SelectContent as UiSelectContent,
  SelectItem as UiSelectItem,
  SelectTrigger as UiSelectTrigger,
  SelectValue as UiSelectValue,
} from "@/components/ui/select"
import { Label as UiLabel } from "@/components/ui/label"
import { cn } from "@/lib/utils"

export type SelectOption = { value: string; label: string; disabled?: boolean }

export type SelectProps = {
  label?: ReactNode
  error?: ReactNode
  helperText?: ReactNode
  options: ReadonlyArray<SelectOption>
  placeholder?: string
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  name?: string
  id?: string
  className?: string
}

export function Select({
  label,
  error,
  helperText,
  options,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  disabled,
  name,
  id,
  className,
}: SelectProps) {
  const reactId = useId()
  const selectId = id ?? `compass-select-${reactId}`
  const helperId = helperText ? `${selectId}-helper` : undefined
  const errorId = error ? `${selectId}-error` : undefined
  const describedBy = [errorId, helperId].filter(Boolean).join(" ") || undefined

  return (
    <div className="flex w-full flex-col gap-1">
      {label ? (
        <UiLabel htmlFor={selectId} className="text-markv-text-700">
          {label}
        </UiLabel>
      ) : null}
      <UiSelect
        {...(value !== undefined ? { value } : {})}
        {...(defaultValue !== undefined ? { defaultValue } : {})}
        {...(onValueChange ? { onValueChange } : {})}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(name !== undefined ? { name } : {})}
      >
        <UiSelectTrigger
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            "w-full border-markv-surface-300 bg-markv-surface-50 text-markv-text-700",
            error ? "border-markv-danger-500 aria-invalid:border-markv-danger-500" : "",
            className,
          )}
        >
          <UiSelectValue placeholder={placeholder} />
        </UiSelectTrigger>
        <UiSelectContent className="border-markv-surface-200 bg-markv-surface-50 text-markv-text-700">
          {options.map((opt) => (
            <UiSelectItem
              key={opt.value}
              value={opt.value}
              {...(opt.disabled !== undefined ? { disabled: opt.disabled } : {})}
            >
              {opt.label}
            </UiSelectItem>
          ))}
        </UiSelectContent>
      </UiSelect>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-markv-danger-accent">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="text-xs text-markv-text-400">
          {helperText}
        </p>
      ) : null}
    </div>
  )
}
